<svelte:options
  customElement={{
    tag: "oc-radio-button-v2",
    extend: window.__components.extend(),
    props: {
      labelPlacement: { type: "String", attribute: "label-placement" },
      widthBehavior: { type: "String", attribute: "width-behavior" },
      size: { type: "String" },
    },
  }}
/>

<script lang="ts">
  import { FormLabelIconV1 } from "../../../common/components/FormLabelIcon";

  import type { Props } from "./RadioButtonV2.types";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";
  import ActionSlot from "../../../common/components/action/ActionSlot.svelte";
  import { useSlots } from "../../../common/utils/useSlots.svelte";
  import ErrorV2 from "../../../common/components/form/ErrorV2.svelte";
  import HintV2 from "../../../common/components/form/HintV2.svelte";
  import type { ActionState } from "../../../common/components/action/ActionSlotProps";

  let { widthBehavior = "fill", labelPlacement = "right", size = "100" }: Props = $props();

  const Host = $host();

  const slots = useSlots(Host);

  let actionState = $state<ActionState>({});
</script>

<div
  class={`radio --size-${size}`}
  class:--invalid={actionState.invalid}
  class:--disabled={actionState.disabled}
  class:--checked={actionState.checked}
  class:--active={actionState.active}
  class:--hover={actionState.hover}
  class:--label-left={labelPlacement === "left"}
>
  <ActionSlot {Host} bind:actionState />
  <div class="icon"></div>
  {#if slots.label}
    <span class="__label">
      <slot name="label" />
      <FormLabelIconV1 variant={actionState.invalid ? "error" : "default"} />
    </span>
  {/if}
</div>

<ErrorV2 />
<HintV2 disabled={actionState.disabled} />
<DefaultStyles display="block" {widthBehavior} />

<style lang="scss">
  @use "@otto-ec/otto-components-utils/scss/mixins";
  @use "@otto-ec/design-tokens/component" as tokens;

  :global {
    .radio {
      --icon-outline-width: 1px;
      --icon-outline-color: #{tokens.$oc-component-form-default-border-color};
      --icon-background-color: #{tokens.$oc-component-form-background-color};
      --icon-color: #{tokens.$oc-component-form-default-icon-color-checked};
      --container-size: #{tokens.$oc-component-form-radio-button-100-container-size};
      --icon-size: #{tokens.$oc-component-form-radio-button-100-icon-size};
      --hitbox-size: #{tokens.$oc-component-form-radio-button-100-hitbox-size};

      font: tokens.$oc-component-form-radio-button-100-label-font;
      display: flex;
      position: relative;
      gap: tokens.$oc-component-form-radio-button-100-gap-x;
      color: tokens.$oc-component-form-default-label-color;
      pointer-events: none; /*                    */
    }

    .--label-left {
      flex-direction: row-reverse;
    }

    .__label {
      flex: 1;
      min-width: 0;
    }

    .icon {
      display: block;
      position: relative;
      margin: calc((var(--hitbox-size) - var(--container-size)) / 2);
      width: var(--container-size);
      height: var(--container-size);

      outline: var(--icon-outline-width) solid var(--icon-outline-color);
      border-radius: 50%;

      background-color: var(--icon-background-color);
      /*                           */
      box-shadow: 0 0 0 1px var(--icon-background-color);
    }

    .--size-50 {
      font: tokens.$oc-component-form-radio-button-50-label-font;
      gap: tokens.$oc-component-form-radio-button-50-gap-x;
      /*                                                                   */
      --icon-size: 8px;
      --container-size: #{tokens.$oc-component-form-radio-button-50-container-size};
      --hitbox-size: #{tokens.$oc-component-form-radio-button-50-hitbox-size};
    }

    /*                   */
    .--checked {
      --icon-background-color: #{tokens.$oc-component-form-background-color-checked};

      .icon:before {
        content: "";
        display: block;
        position: absolute;
        inset: calc((var(--container-size) - var(--icon-size)) / 2);
        border-radius: 50%;
        background-color: var(--icon-color);
      }
    }

    /*                 */
    .--hover {
      --icon-outline-width: 2px;
    }

    /*                  */
    .--active {
      --icon-outline-width: 2px;
      --icon-outline-color: #{tokens.$oc-component-form-default-border-color-active};
    }

    /*                   */
    .--invalid {
      color: tokens.$oc-component-form-error-label-color;
      --icon-outline-color: #{tokens.$oc-component-form-error-border-color};

      &.--checked {
        --icon-background-color: #{tokens.$oc-component-form-error-icon-color};
      }
    }

    /*                    */
    .radio.--disabled {
      color: tokens.$oc-component-form-disabled-label-color;
      --icon-outline-width: 1px;
      --icon-outline-color: #{tokens.$oc-component-form-disabled-border-color};
      --icon-background-color: #{tokens.$oc-component-form-disabled-background-color};
      --icon-color: #{tokens.$oc-component-form-disabled-icon-color};
    }
  }
</style>
